// SPDX-License-Identifier: BUSL-1.1

/**
 * Maps audit entries to natural-language display parts for the ActivityFeed.
 *
 */

import type { AuditEntry } from '@/types/api';

export interface FormattedAuditAction {
  /** Verb phrase, e.g. 'promoted', 'drafted', 'toggled'. */
  verb: string;
  /** Human-readable resource label, e.g. 'sdui:score-delta'. */
  resource: string;
  /** Color variant for the ResourcePill. */
  variant: 'blue' | 'green' | 'amber' | 'red' | 'purple';
  /** Optional trailing text, e.g. 'to production'. */
  suffix?: string;
}

/**
 * Parses entry.action (e.g. 'config.promote') + entry.resourceType/resourceId
 * into a display-ready shape. Pure — no side effects, no hooks.
 *
 * Action categories:
 * - github.* — GitHub events (push, PR, review, release)
 * - agent.*  — Agent activity (episode synthesized, decision, sync)
 * - config.* — Config lifecycle (create, stage, promote, rollback)
 * - flag.*   — Flag operations (toggle, kill_switch)
 * - cache.*  — Cache invalidation
 * - session.* — Agent session lifecycle
 */
export function formatAuditAction(entry: AuditEntry): FormattedAuditAction {
  const actionSuffix = entry.action.split('.').pop() ?? entry.action;
  const actionPrefix = entry.action.split('.').slice(0, -1).join('.');
  const resource = `${entry.resourceType}:${entry.resourceId}`;
  const meta = typeof (entry as unknown as Record<string, unknown>).metadata === 'object' &&
    (entry as unknown as Record<string, unknown>).metadata !== null &&
    !Array.isArray((entry as unknown as Record<string, unknown>).metadata)
    ? ((entry as unknown as Record<string, unknown>).metadata as Record<string, unknown>)
    : undefined;

  // ── GitHub events (Sprint 44) ─────────────────────────────────────────
  if (actionPrefix === 'github' || actionPrefix === 'github.pr' || actionPrefix === 'github.push') {
    switch (actionSuffix) {
      case 'push': {
        const commits = meta?.commits_count as number | undefined;
        const branch = meta?.ref as string | undefined;
        return {
          verb: 'pushed to',
          resource,
          variant: 'purple',
          suffix: commits ? `${commits} commit${commits === 1 ? '' : 's'}${branch ? ` on ${branch}` : ''}` : undefined,
        };
      }
      case 'pr_opened': {
        const title = meta?.title as string | undefined;
        const prNum = meta?.number as number | undefined;
        return {
          verb: `opened PR${prNum ? ` #${prNum}` : ''}`,
          resource,
          variant: 'purple',
          suffix: title ? `"${title}"` : undefined,
        };
      }
      case 'pr_merged': {
        const title = meta?.title as string | undefined;
        const prNum = meta?.number as number | undefined;
        return {
          verb: `merged PR${prNum ? ` #${prNum}` : ''}`,
          resource,
          variant: 'purple',
          suffix: title ? `"${title}"` : undefined,
        };
      }
      case 'pr_review': {
        const state = meta?.state as string | undefined;
        const prNum = meta?.number as number | undefined;
        return {
          verb: `reviewed PR${prNum ? ` #${prNum}` : ''}`,
          resource,
          variant: 'purple',
          suffix: state ? `— ${state}` : undefined,
        };
      }
      case 'release': {
        const tag = meta?.tag_name as string | undefined;
        return {
          verb: 'published release',
          resource,
          variant: 'purple',
          suffix: tag ?? undefined,
        };
      }
      case 'issue': {
        const title = meta?.title as string | undefined;
        const action = meta?.action as string | undefined;
        return {
          verb: `${action ?? 'updated'} issue`,
          resource,
          variant: 'purple',
          suffix: title ? `"${title}"` : undefined,
        };
      }
    }
  }

  // ── Agent activity (Sprint 44) ───────────────────────────────────────
  if (actionPrefix === 'agent' || actionPrefix === 'episode' || actionPrefix === 'decision') {
    switch (actionSuffix) {
      case 'episode_synthesized': {
        const title = meta?.episode_title as string | undefined;
        const sessions = meta?.sessions_count as number | undefined;
        const decisions = meta?.decisions_count as number | undefined;
        let suffix = '';
        if (sessions) suffix += `${sessions} session${sessions === 1 ? '' : 's'}`;
        if (decisions) suffix += suffix ? `, ${decisions} decision${decisions === 1 ? '' : 's'}` : `${decisions} decision${decisions === 1 ? '' : 's'}`;
        return {
          verb: 'synthesized episode',
          resource,
          variant: 'green',
          suffix: title ? `"${title}"${suffix ? ` · ${suffix}` : ''}` : (suffix || undefined),
        };
      }
      case 'decision_made': {
        const statement = meta?.statement as string | undefined;
        return {
          verb: 'recorded decision',
          resource,
          variant: 'green',
          suffix: statement ? `"${statement.slice(0, 60)}${statement.length > 60 ? '…' : ''}"` : undefined,
        };
      }
      case 'sync_complete': {
        const workspace = meta?.workspace_name as string | undefined;
        return {
          verb: 'synced',
          resource,
          variant: 'green',
          suffix: workspace ? `from ${workspace}` : undefined,
        };
      }
      case 'session_start':
        return { verb: 'started', resource, variant: 'green' };
      case 'session_end':
        return { verb: 'ended', resource, variant: 'purple' };
      case 'budget_exceeded':
        return { verb: 'budget exceeded on', resource, variant: 'red' };
    }
  }

  switch (actionSuffix) {
    // Config lifecycle
    case 'create':
      return { verb: 'drafted', resource, variant: 'amber' };
    case 'stage':
      return { verb: 'staged', resource, variant: 'blue' };
    case 'promote':
      return { verb: 'promoted', resource, variant: 'green', suffix: 'to production' };
    case 'rollback':
      return { verb: 'rolled back', resource, variant: 'red' };

    // Flags
    case 'toggle':
      return { verb: 'toggled', resource, variant: 'blue' };
    case 'kill_switch':
      return { verb: 'killed', resource, variant: 'red' };

    // Cache
    case 'invalidate':
      return { verb: 'invalidated', resource, variant: 'blue' };

    // Sessions / agents
    case 'session_start':
      return { verb: 'started', resource, variant: 'green' };
    case 'session_end':
      return { verb: 'ended', resource, variant: 'purple' };
    case 'budget_exceeded':
      return { verb: 'budget exceeded on', resource, variant: 'red' };

    // Fallback: show raw action suffix
    default:
      return { verb: actionSuffix, resource, variant: 'blue' };
  }
}
